/**
 * Public deal-landing page. URL: `app.marketpryce.com/<deal-slug>?r=<token>`.
 *
 * Clean single-column layout on a light (frost) surface — brand logo
 * circle, personalized headline, and signup form.
 */
import { Redirect, Stack, useLocalSearchParams } from 'expo-router';
import Head from 'expo-router/head';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import NotFoundScreen from '@/app/+not-found';
import { SignupCard } from '@/components/SignupCard';
import { colors, spacing } from '@/constants/theme';
import { buildOg } from '@/lib/dealOg';
import { RESERVED_SLUGS } from '@/lib/reservedSlugs';
import { trackViewContent } from '@/services/metaPixel';
import {
  publicSignupService,
  type PublicSignupDeal,
  type ReferralInviteResponse,
} from '@/services/publicSignup';
import { authStore } from '@/stores/auth.store';

// Reserved root-path slugs live in lib/reservedSlugs.ts (shared with the
// share-preview middleware).

const PublicDealLandingScreen = observer(() => {
  const params = useLocalSearchParams<{ slug?: string; r?: string; ref?: string }>();
  const slug = typeof params.slug === 'string' ? params.slug : undefined;
  const referralToken = typeof params.r === 'string' ? params.r : undefined;
  const refId = typeof params.ref === 'string' ? params.ref : undefined;

  const [deal, setDeal] = useState<PublicSignupDeal | null>(null);
  const [dealLoading, setDealLoading] = useState<boolean>(true);
  const [dealNotFound, setDealNotFound] = useState<boolean>(false);
  const [invite, setInvite] = useState<ReferralInviteResponse | null>(null);

  useEffect(() => {
    if (!slug || RESERVED_SLUGS.has(slug)) return;
    let cancelled = false;
    setDealLoading(true);
    publicSignupService
      .fetchDealBySlug(slug)
      .then((d) => {
        if (cancelled) return;
        if (d) setDeal(d);
        else setDealNotFound(true);
      })
      .catch(() => {
        if (!cancelled) setDealNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setDealLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!deal) return;
    trackViewContent({ dealSlug: deal.slug, brandName: deal.brand_name });
  }, [deal]);

  useEffect(() => {
    // Fire the invite fetch for either URL shape: `?r=<token>` (new
    // app shares) or `?ref=<ref_id|handle|email>` (legacy Webflow 301
    // + old marketing emails). Backend's referral_invites#show falls
    // back from token lookup to `lookup_user_by_ref` so the same
    // endpoint serves both — the inviter chip ("Jordan referred you")
    // renders on both share shapes.
    const identifier = referralToken ?? refId;
    if (!identifier) return;
    let cancelled = false;
    publicSignupService
      .invite(identifier, slug)
      .then((data) => {
        if (!cancelled) setInvite(data);
      })
      .catch(() => {
        // Silent: page degrades gracefully without the inviter chip.
      });
    return () => {
      cancelled = true;
    };
  }, [referralToken, refId, slug]);

  if (typeof slug !== 'string' || RESERVED_SLUGS.has(slug)) {
    return <NotFoundScreen />;
  }

  if (authStore.isAuthenticated) {
    return <Redirect href={{ pathname: '/deals/[slug]', params: { slug } }} />;
  }

  // Pre-hydrate we don't yet know whether this visitor is authed (the
  // redirect above would fire once hydrate() resolves). Keep rendering
  // the page shell + Head — static export depends on it for OG tags —
  // but hold the card in its loading state so authed users don't see a
  // signup-card flash before the redirect.
  const cardLoading = dealLoading || !authStore.hydrated;

  if (!dealLoading && dealNotFound) {
    return <NotFoundScreen />;
  }

  const inviter = invite?.inviter ?? null;

  // Always render a Head — start with slug-derived defaults so static
  // export captures usable OG tags before the deal fetch resolves;
  // deal-specific fields override once `deal` is set.
  const og = buildOg(slug, deal);

  return (
    <View style={styles.root}>
      <Image
        source={require('@/assets/images/athlete-bg.jpg')}
        style={styles.pageBg}
        resizeMode="cover"
      />
      <View style={styles.pageOverlay} />

      <Stack.Screen options={{ headerShown: false }} />
      <Head>
        <title>{og.title}</title>
        <meta name="description" content={og.description} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={og.title} />
        <meta property="og:description" content={og.description} />
        <meta property="og:image" content={og.image} />
        <meta property="og:url" content={og.url} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={og.title} />
        <meta name="twitter:description" content={og.description} />
        <meta name="twitter:image" content={og.image} />
      </Head>

      <SafeAreaView style={styles.safe} edges={['top', 'bottom']}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.flex}
        >
          <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
            <View style={styles.cardWrap}>
              <SignupCard
                deal={deal}
                inviter={inviter}
                referralToken={referralToken}
                refId={refId}
                pathSlug={slug}
                loading={cardLoading}
              />
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
});

export default PublicDealLandingScreen;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.deepGreen },
  pageBg: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  pageOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.deepGreenOverlay,
  },
  safe: { flex: 1 },
  flex: { flex: 1 },
  scroll: {
    flexGrow: 1,
    alignItems: 'center',
    paddingTop: spacing['2xl'],
    paddingBottom: spacing.xl,
  },
  cardWrap: {
    width: '100%',
    alignItems: 'center',
    paddingHorizontal: spacing.base,
  },
});
